import { UserRole } from "@features/settings/types/settings.types";
import ModalBlank from "@shared/components/modals/ModalBlank";
import { Button } from "@shared/components/ui/Button";
import { useProfile } from "@shared/hooks/useProfile";
import AuthenticatedLayout from "@shared/layouts/AuthenticatedLayout";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ResourceCard from "../components/ResourceCard";
import ResourceForm from "../components/ResourceForm";
import { ResourcePagination } from "../components/ResourcePagination";
import { useResourcesStore } from "../store/useResourcesStore";
import {
  CreateResourceData,
  Resource,
  ResourceSortType,
  UpdateResourceData,
} from "../types/resource.types";

const ResourcesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useProfile();
  const isAdmin = user?.role === UserRole.ADMIN;

  const [limit, setLimit] = useState(12);

  // Modal states
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreatedNotice, setShowCreatedNotice] = useState(false);

  // Auto-open create modal when navigated with ?create=1
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("create") === "1") {
      setSelectedResource(null);
      setShowResourceModal(true);
      navigate("/resources", { replace: true });
    }
  }, [location.search, navigate]);

  const {
    resources,
    totalResources,
    currentPage,
    isLoading,
    error,
    sortType,
    fetchResources,
    searchResources,
    searchResults,
    totalSearchResults,
    currentSearchPage,
    isSearching,
    searchError,
    setSortType,
  } = useResourcesStore();

  // Search term state
  const [searchTerm, setSearchTerm] = useState("");

  // Calculate total pages
  const totalPages = Math.ceil(
    (searchTerm ? totalSearchResults : totalResources) / limit,
  );

  // Use a single ref to track all data fetching states
  const fetchStateRef = useRef({
    initialFetchDone: false,
    lastSearchTerm: "",
    lastLimit: limit,
    isFetching: false,
  });

  // Single effect to handle all data fetching scenarios
  useEffect(() => {
    const fetchState = fetchStateRef.current;

    // Prevent concurrent fetches
    if (fetchState.isFetching) return;

    // Handle initial fetch
    if (!fetchState.initialFetchDone) {
      fetchState.isFetching = true;
      fetchResources(1, limit).finally(() => {
        fetchState.initialFetchDone = true;
        fetchState.isFetching = false;
      });
      return;
    }

    // Handle search term changes
    if (searchTerm !== fetchState.lastSearchTerm) {
      fetchState.lastSearchTerm = searchTerm;
      fetchState.isFetching = true;

      if (searchTerm.trim()) {
        searchResources(searchTerm, 1, limit).finally(() => {
          fetchState.isFetching = false;
        });
      } else {
        fetchResources(1, limit).finally(() => {
          fetchState.isFetching = false;
        });
      }
      return;
    }

    // Handle limit changes (only if not triggered by search or initial load)
    if (limit !== fetchState.lastLimit && fetchState.initialFetchDone) {
      fetchState.lastLimit = limit;
      fetchState.isFetching = true;

      if (searchTerm.trim()) {
        searchResources(searchTerm, 1, limit).finally(() => {
          fetchState.isFetching = false;
        });
      } else {
        fetchResources(1, limit).finally(() => {
          fetchState.isFetching = false;
        });
      }
    }
  }, [searchTerm, limit, fetchResources, searchResources]);

  // Handle page change
  const handlePageChange = (page: number) => {
    if (searchTerm) {
      searchResources(searchTerm, page, limit);
    } else {
      fetchResources(page, limit);
    }
  };

  // Handle limit change
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    if (searchTerm) {
      searchResources(searchTerm, 1, newLimit);
    } else {
      fetchResources(1, newLimit);
    }
  };

  // Handle sort change
  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortType(e.target.value as ResourceSortType);
  };

  // Handle resource click
  const handleResourceClick = (resourceId: string) => {
    navigate(`/resources/${resourceId}`);
  };

  // Open resource form modal for creating a new resource
  const openCreateResourceModal = () => {
    setSelectedResource(null);
    setShowResourceModal(true);
  };

  // Open resource form modal for editing an existing resource
  const openEditResourceModal = (resource: Resource) => {
    setSelectedResource(resource);
    setShowResourceModal(true);
  };

  // Determine which resources to display
  const displayedResources = searchTerm ? searchResults : resources;
  const displayError = searchTerm ? searchError : error;

  return (
    <AuthenticatedLayout
      pageHeader={{
        title: "Resources",
        actions: [
          {
            label: "Add Resource",
            onClick: openCreateResourceModal,
          },
          ...(isAdmin
            ? [
                {
                  label: "Resource Management",
                  onClick: () => navigate("/resources/management"),
                  variant: "orange" as const,
                },
              ]
            : [
                {
                  label: "My Resources",
                  onClick: () => navigate("/resources/my-resources"),
                  variant: "orange" as const,
                },
              ]),
        ],
        search: {
          show: true,
          placeholder: "Search resources...",
          onChange: setSearchTerm,
        },
      }}
      isLoading={searchTerm ? isSearching : isLoading}
      error={displayError ? new Error(displayError) : null}
    >
      <div className="px-4 sm:px-6 lg:px-8 py-4 w-full max-w-9xl mx-auto">
        {/* Sort options */}
        {/* <div className="flex justify-end mb-6">
          <div className="flex items-center space-x-2">
            <label htmlFor="sort-by" className="text-sm text-gray-600">Sort by:</label>
            <select
              id="sort-by"
              value={sortType}
              onChange={handleSortChange}
              className="border border-gray-300 rounded-md text-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={ResourceSortType.NEWEST}>Newest</option>
              <option value={ResourceSortType.POPULAR}>Popular</option>
            </select>
          </div>
        </div> */}

        {/* No results */}
        {!isLoading && !isSearching && displayedResources.length === 0 && (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-gray-100">
              No resources found
            </h3>
            <p className="mt-1 text-gray-500 dark:text-gray-400">
              {searchTerm
                ? "Try a different search term"
                : "Create your first resource to get started"}
            </p>
          </div>
        )}

        {/* Resources grid */}
        {displayedResources.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-6">
            {displayedResources.map((resource) => (
              <ResourceCard
                key={resource.id}
                resource={resource}
                onClick={handleResourceClick}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {displayedResources.length > 0 && totalPages > 1 && (
          <ResourcePagination
            currentPage={searchTerm ? currentSearchPage : currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            limit={limit}
            onLimitChange={handleLimitChange}
          />
        )}
      </div>

      {/* Resource Form Modal */}
      <ModalBlank
        id="resource-form-modal"
        className="max-h-[80vh] overflow-y-auto p-4"
        modalOpen={showResourceModal}
        setModalOpen={setShowResourceModal}
        showCloseButton={true}
      >
        <ResourceForm
          resource={selectedResource || undefined}
          onSubmit={handleResourceSubmit}
          onDelete={
            selectedResource && canDeleteResource()
              ? handleDeleteResource
              : undefined
          }
          onCancel={() => setShowResourceModal(false)}
          isSubmitting={isSubmitting}
          canEdit={canEditResource()}
          canDelete={canDeleteResource()}
        />
      </ModalBlank>

      {/* Post-create notice modal — non-admin only */}
      <ModalBlank
        id="resource-created-notice"
        className="p-0 max-w-md w-full rounded-xl overflow-hidden"
        modalOpen={showCreatedNotice}
        setModalOpen={setShowCreatedNotice}
        showCloseButton={false}
      >
        <div className="relative">
          {/* Close X */}
          <button
            onClick={() => setShowCreatedNotice(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Icon */}
          <div className="flex flex-col items-center text-center px-8 pt-10 pb-8">
            <div className="w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-5">
              <svg
                className="w-8 h-8 text-blue-500 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Resource Submitted for Review
            </h3>

            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-2">
              Your resource has been successfully created and is now pending
              approval by the GCCA team.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-8">
              Our administrators will review your submission and determine
              whether it meets our publishing guidelines. Once a decision has
              been made, you'll be notified both in-app and via email with the
              outcome.
            </p>

            <div className="flex flex-col gap-3 w-full">
              <Button
                variant="primary"
                fullWidth
                onClick={() => {
                  setShowCreatedNotice(false);
                  navigate("/resources/my-resources");
                }}
              >
                View My Resources
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setShowCreatedNotice(false)}
              >
                Got it, thanks!
              </Button>
            </div>
          </div>
        </div>
      </ModalBlank>
    </AuthenticatedLayout>
  );

  // Handle resource creation/update
  async function handleResourceSubmit(
    data: CreateResourceData | UpdateResourceData,
  ) {
    setIsSubmitting(true);
    try {
      if (selectedResource) {
        // Update existing resource
        await useResourcesStore
          .getState()
          .updateResource(selectedResource.id!, data as UpdateResourceData);
      } else {
        // Create new resource
        await useResourcesStore
          .getState()
          .createResource(data as CreateResourceData);
        if (!isAdmin) {
          setShowCreatedNotice(true);
        }
      }
      setShowResourceModal(false);
      // Refresh resources list
      fetchResources(1, limit);
    } catch (error) {
      console.error("Error submitting resource:", error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }

  // Handle resource deletion
  async function handleDeleteResource() {
    setIsSubmitting(true);
    try {
      if (selectedResource?.id) {
        await useResourcesStore.getState().deleteResource(selectedResource.id);
        setShowResourceModal(false);
        // Refresh resources list
        fetchResources(1, limit);
      }
    } catch (error) {
      console.error("Error deleting resource:", error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }

  // Check if current user can edit the resource
  function canEditResource() {
    if (!selectedResource) return true; // New resource can be edited
    if (!user) return false;
    return isAdmin || selectedResource.authorId === user.id;
  }

  // Check if current user can delete the resource
  function canDeleteResource() {
    if (!selectedResource) return false; // New resource can't be deleted
    if (!user) return false;
    return isAdmin || selectedResource.authorId === user.id;
  }
};

export default ResourcesPage;
